import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FlexStack, Text } from '~/components/atomic';
import { useGetCommentListQuery } from '~/features/comment';
import { useModal } from '~/hooks';
import { GetCommentData, ModalParams } from '~/types';
import NoCommentImage from '~/assets/img/completion_images/no_comment.png';
import { useUserStore } from '~/store';
import { CommentProps } from '../CommentContainer';
import CommentInfo from './CommentInfo';
import CommentInput from './CommentInput';
import CommentList from './CommentList';

export type SortComment = 'newest' | 'top';

interface CommentInnerProps {
  isCommentOpen: boolean;
  commentInputState: string;
  commentCount: CommentProps['viewComment']['total'];
  setIsCommentOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCommentInputState: React.Dispatch<React.SetStateAction<string>>;

  editingComment: GetCommentData | null;
  setEditingComment: React.Dispatch<
    React.SetStateAction<GetCommentData | null>
  >;
  isEditMode: boolean;
  isMissionGame?: boolean;
}

export default function CommentInner({
  isCommentOpen,
  commentInputState,
  setIsCommentOpen,
  setCommentInputState,
  editingComment,
  setEditingComment,
  isEditMode,
  isMissionGame = false,
}: CommentInnerProps) {
  const email = useUserStore((state) => state.email);
  const { id: targetId = '' } = useParams<{ id: string }>(); // 캐릭터나 게임 아이디
  const [sortOrder, setSortOrder] = useState<SortComment>('top');
  const { openModal } = useModal();
  const inputRef = useRef<HTMLDivElement>(null);

  const getCommentListData = isMissionGame
    ? {
        game_id: Number(targetId),
        email,
        sort_by: sortOrder,
      }
    : {
        character_id: targetId,
        email,
        sort_by: sortOrder,
      };

  const { data, isFetchingNextPage, fetchNextPage } =
    useGetCommentListQuery(getCommentListData);

  const toggleSortComments = () => {
    setSortOrder((prevOrder) => (prevOrder === 'newest' ? 'top' : 'newest'));
  };

  const handleEditModal = () => {
    const modalEditRequest: ModalParams = {
      title: 'Delete changes',
      content: 'Do you want to delete your changes?',
      click: () => {
        setEditingComment(null);
        setCommentInputState('');
      },
      buttonName: 'Delete',
      leftButtonName: 'Keep edit',
    };

    openModal(modalEditRequest);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditMode &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        handleEditModal();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditMode, editingComment]);

  return (
    <FlexStack
      css={css`
        justify-content: space-between;
        width: 100%;
        height: 100%;
      `}
    >
      <FlexStack
        css={css`
          flex-shrink: 0;
        `}
      >
        <CommentInfo
          commentCount={data.total as number}
          setIsCommentOpen={setIsCommentOpen}
          sortOrder={sortOrder}
          toggleSortComments={toggleSortComments}
        />
      </FlexStack>
      {data.total === 0 ? (
        <NoComment>
          <img src={NoCommentImage} alt='no_comment_image' />
          <Text as={'p'} fontStyle='text.body16_medium' color='content_light_2'>
            Add a first comment now!
          </Text>
        </NoComment>
      ) : (
        <CommentList
          allList={data}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          editingComment={editingComment}
          setEditingComment={setEditingComment}
          sortOrder={sortOrder}
          isMissionGame={isMissionGame}
        />
      )}

      <CommentInputContainer ref={inputRef}>
        <CommentInput
          isCommentOpen={isCommentOpen}
          target_id={targetId}
          commentInputState={commentInputState}
          setCommentInputState={setCommentInputState}
          editingComment={editingComment}
          setEditingComment={setEditingComment}
          isEditMode={isEditMode}
          sort={sortOrder}
          isMissionGame={isMissionGame}
        />
      </CommentInputContainer>
    </FlexStack>
  );
}

export const NoComment = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  gap: 8px;

  & img {
    width: 80%;
    max-width: 250px;
  }
`;

export const CommentInputContainer = styled.div`
  display: flex;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
`;
